// CONTRACT:C1-MAP-CLIENT.1.0
// COP — Google OIDC client (PKCE) + TDFLite federation exchange.
//
// "Sign in with Google" wiring. This module owns ONLY the browser-side OIDC
// dance and the federation POST; it derives NO clearance itself. The clearance
// on the returned session comes entirely from the TDFLite JWT that the TRUSTED
// IdP mints from the verified email domain (see internal/idplite/federation.go
// in the TDFLite-tak fork). The client merely transports the authorization code.
//
// Flow (BFF — server-side code exchange):
//   1. beginGoogleSignIn()       — PKCE: stash verifier+state+nonce, redirect to Google.
//   2. (Google redirects back with ?code&state)
//   3. exchangeCodeForSession()  — verify state (CSRF), then POST {code, code_verifier,
//      redirect_uri, nonce} to /idp/federate/google. The browser NEVER calls
//      Google's token endpoint: a Google "Web application" client requires the
//      confidential client_secret a SPA must not hold (that call 400s with
//      "client_secret missing"). The IdP redeems the code server-side, verifies
//      the resulting id_token, and mints a TDFLite session.
//   4. completeGoogleRedirect()  — the loop-safe orchestration the login screen runs:
//      it strips ?code/&state off the URL BEFORE anything (a reload can't re-fire it)
//      and NEVER auto-retries on failure.
//
// Every network seam and storage seam is injectable so the wiring is unit-
// testable WITHOUT a real Google OAuth client (mock the IdP).
//
// Type-only imports are erased at build time, so this module stays free of the
// heavy @opentdf/sdk graph that tdflite.ts/kas.ts pull in.

import type { Session } from "./tdflite.ts";
import type { SubjectClaims } from "../../../../packages/abac-pdp/src/index.ts";

/** Google's OAuth 2.0 / OIDC authorization endpoint. The token endpoint is NOT
 *  referenced here on purpose: the code→token exchange is done SERVER-SIDE by the
 *  TDFLite IdP (BFF), because it needs the confidential client_secret a SPA must
 *  not hold. */
export const GOOGLE_AUTH_ENDPOINT = "https://accounts.google.com/o/oauth2/v2/auth";

/** The registered Google OAuth client id. A placeholder by default — live e2e
 *  needs a REAL client id (+ authorized origins/redirect) from the Google
 *  console; see isPlaceholderGoogleClient(). */
export const GOOGLE_CLIENT_ID =
  (import.meta.env?.VITE_GOOGLE_CLIENT_ID as string | undefined) ??
  "REPLACE_WITH_GOOGLE_OAUTH_CLIENT_ID.apps.googleusercontent.com";

/** TDFLite gateway base (same-origin "" by default; mirrors tdflite.ts). */
const FED_BASE = (import.meta.env?.VITE_TDFLITE_BASE as string | undefined) ?? "";
/** client_id stamped on the minted token (the OIDC client KAS accepts for rewrap). */
const FED_CLIENT_ID = (import.meta.env?.VITE_TDFLITE_CLIENT_ID as string | undefined) ?? "opentdf-public";

const PKCE_KEY = "tak-tdf.google.pkce";

/** True when no real Google client id has been configured — the live redirect
 *  will fail at Google, so callers can surface a "needs setup" hint instead. */
export function isPlaceholderGoogleClient(): boolean {
  return GOOGLE_CLIENT_ID.startsWith("REPLACE_WITH_");
}

// --- crypto / encoding ---

function b64url(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** A high-entropy URL-safe random string (default 32 bytes → 43 chars). */
export function randomString(bytes = 32): string {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return b64url(buf);
}

/** RFC 7636 S256 code challenge = base64url(SHA-256(verifier)). */
export async function pkceChallengeFromVerifier(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return b64url(new Uint8Array(digest));
}

// --- authorization request ---

export interface AuthUrlOpts {
  clientId: string;
  redirectUri: string;
  state: string;
  nonce: string;
  codeChallenge: string;
  scope?: string;
  /** The OAuth `prompt`. Default "select_account" (interactive sign-in). The SILENT
   *  re-auth path passes "none" — Google returns a code IFF the user still has a live
   *  session (else `error=login_required`), with NO interactive UI. */
  prompt?: string;
  /** OAuth `login_hint` — the email of the session we want re-verified. Threaded on the
   *  silent path so Google resolves the RIGHT session under prompt=none (a multi-session
   *  browser otherwise can't disambiguate without UI). Omitted ⇒ not sent. */
  loginHint?: string;
}

/** Build the Google authorization-code URL (PKCE, S256). Pure. */
export function buildGoogleAuthUrl(o: AuthUrlOpts): string {
  const p = new URLSearchParams({
    client_id: o.clientId,
    redirect_uri: o.redirectUri,
    response_type: "code",
    scope: o.scope ?? "openid email profile",
    code_challenge: o.codeChallenge,
    code_challenge_method: "S256",
    state: o.state,
    nonce: o.nonce,
    prompt: o.prompt ?? "select_account",
    include_granted_scopes: "true",
  });
  if (o.loginHint) p.set("login_hint", o.loginHint);
  return `${GOOGLE_AUTH_ENDPOINT}?${p.toString()}`;
}

/** The redirect_uri — this exact page (must be an Authorized redirect URI in
 *  the Google console). */
export function googleRedirectUri(): string {
  return `${window.location.origin}${window.location.pathname}`;
}

export interface BeginDeps {
  storage?: Storage;
  assign?: (url: string) => void;
  clientId?: string;
  redirectUri?: string;
}

/** Kick off "Sign in with Google": generate PKCE material, persist it, and
 *  navigate to Google. Seams are injectable for tests. */
export async function beginGoogleSignIn(deps: BeginDeps = {}): Promise<void> {
  const storage = deps.storage ?? sessionStorage;
  const clientId = deps.clientId ?? GOOGLE_CLIENT_ID;
  const redirectUri = deps.redirectUri ?? googleRedirectUri();

  const verifier = randomString(48);
  const codeChallenge = await pkceChallengeFromVerifier(verifier);
  const state = randomString(16);
  const nonce = randomString(16);

  storage.setItem(PKCE_KEY, JSON.stringify({ verifier, state, nonce, redirectUri }));
  const url = buildGoogleAuthUrl({ clientId, redirectUri, state, nonce, codeChallenge });
  (deps.assign ?? ((u: string) => window.location.assign(u)))(url);
}

// --- callback handling ---

export interface GoogleCallback {
  code?: string;
  state?: string;
  error?: string;
}

/** Parse the OAuth redirect params off a URL. Pure. */
export function readGoogleCallback(href: string): GoogleCallback {
  const u = new URL(href);
  const q = u.searchParams;
  return {
    code: q.get("code") ?? undefined,
    state: q.get("state") ?? undefined,
    error: q.get("error") ?? undefined,
  };
}

// --- federation exchange (Google authorization code → TDFLite session, via the IdP BFF) ---

/** Decode a JWT payload (no verification — the IdP signed it; KAS verifies for
 *  security decisions). Mirrors tdflite.ts's private decoder. */
function decodeJwtPayload(token: string): Record<string, unknown> {
  const [, payload] = token.split(".");
  const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
  return JSON.parse(json) as Record<string, unknown>;
}

/** Compact clearance summary for the UI (mirrors tdflite.ts's summarize). */
function summarizeClearance(claims: Record<string, unknown>): string {
  const cls = String(claims.classification ?? "").toUpperCase();
  const arr = (k: string): string[] => (Array.isArray(claims[k]) ? (claims[k] as string[]) : []);
  const sci = arr("sci_compartment").map((s) => s.toUpperCase());
  const rel = arr("releasability").map((s) => s.toUpperCase());
  const cav = arr("handling_caveat").map((s) => s.toUpperCase());
  const parts = [cls];
  if (sci.length) parts.push(sci.join("/"));
  if (cav.length) parts.push(cav.join("/"));
  if (rel.length) parts.push("REL " + rel.join(","));
  return parts.filter(Boolean).join(" · ");
}

export interface CodeExchangeDeps {
  fetchImpl?: typeof fetch;
  storage?: Storage;
  base?: string;
  clientId?: string;
}

/** Exchange the Google authorization code for a TDFLite session via the TRUSTED
 *  IdP's BFF endpoint. The browser NEVER calls Google's token endpoint — a Google
 *  "Web application" client requires a confidential `client_secret` a SPA must not
 *  hold (the browser-side exchange 400s with "client_secret missing"). Instead it
 *  ships {code, code_verifier, redirect_uri, nonce} to /idp/federate/google, which
 *  performs the server-side code→id_token exchange with the secret, verifies the
 *  id_token (sig/iss/aud/exp/email_verified + nonce binding + replay), derives
 *  clearance from the verified email DOMAIN, and mints a TDFLite JWT. The
 *  clearance is assigned by the IdP — never by this client.
 *
 *  The OAuth `state` is verified client-side first (CSRF) — the binding the
 *  browser owns. The stashed PKCE verifier + per-sign-in nonce ride along so the
 *  IdP can bind the token to THIS attempt. */
export async function exchangeCodeForSession(cb: GoogleCallback, deps: CodeExchangeDeps = {}): Promise<Session> {
  if (cb.error) throw new Error(`google sign-in error: ${cb.error}`);
  if (!cb.code) throw new Error("google sign-in: no authorization code in callback");

  const storage = deps.storage ?? sessionStorage;
  const raw = storage.getItem(PKCE_KEY);
  if (!raw) throw new Error("google sign-in: no PKCE state — start the sign-in again");
  const st = JSON.parse(raw) as { verifier: string; state: string; nonce: string; redirectUri: string };
  // CSRF: the OAuth `state` echoed by Google MUST equal the one we stashed before
  // redirecting. Checked BEFORE any network call.
  if (!cb.state || st.state !== cb.state) throw new Error("google sign-in: state mismatch (possible CSRF) — aborting");

  const fetchImpl = deps.fetchImpl ?? fetch;
  const base = deps.base ?? FED_BASE;
  const clientId = deps.clientId ?? FED_CLIENT_ID;

  const resp = await fetchImpl(`${base}/idp/federate/google`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      code: cb.code,
      code_verifier: st.verifier,
      redirect_uri: st.redirectUri,
      nonce: st.nonce,
      client_id: clientId,
    }),
  });
  const j = (await resp.json()) as Record<string, unknown>;
  if (!resp.ok || typeof j.access_token !== "string") {
    throw new Error(String(j.error_description ?? j.error ?? `google federation failed (${resp.status})`));
  }

  // The authorization code is single-use and now spent; drop the PKCE material.
  storage.removeItem(PKCE_KEY);
  return sessionFromAccessToken(
    j.access_token,
    typeof j.refresh_token === "string" ? j.refresh_token : undefined,
  );
}

export interface CompleteRedirectDeps {
  /** Clear the OAuth `?code`/`&state` off the URL (history.replaceState to the
   *  bare path). Called UNCONDITIONALLY before the exchange so a reload / re-render
   *  can never re-trigger the federation — the infinite-loop kill. */
  clearUrl: () => void;
  /** Run the code→session exchange. In the app this is `tdflite.federateGoogle`
   *  (transport + session persistence + cert hygiene); tests inject a fake. */
  exchange: (cb: GoogleCallback) => Promise<Session>;
}

/** Complete a "Sign in with Google" redirect landing — the loop-safe orchestration
 *  the login screen runs on mount:
 *
 *   1. ALWAYS clear the `?code`/`&state` off the URL FIRST (before any await), so a
 *      reload / re-render / re-mount can never re-fire the exchange.
 *   2. Run the IdP code→session exchange.
 *   3. On ANY failure, return the error — and NEVER auto-retry / re-initiate the
 *      sign-in (there is no path back into beginGoogleSignIn). The caller just
 *      shows the error.
 *
 *  Returns `{ session }` on success or `{ error }` on failure. */
export async function completeGoogleRedirect(
  cb: GoogleCallback,
  deps: CompleteRedirectDeps,
): Promise<{ session: Session } | { error: string }> {
  // KILL THE LOOP: strip the OAuth params BEFORE anything else.
  deps.clearUrl();
  try {
    return { session: await deps.exchange(cb) };
  } catch (e) {
    // Fail closed to a surfaced error — do NOT re-initiate the sign-in.
    return { error: e instanceof Error ? e.message : "google sign-in failed" };
  }
}

/** Turn an IdP-minted TDFLite access token into a COP Session. The clearance/ABAC
 *  claims come from the JWT (assigned by the IdP from the email domain); the
 *  Google profile name/picture ride along for the avatar. */
function sessionFromAccessToken(token: string, refreshToken?: string): Session {
  const claims = decodeJwtPayload(token);
  const username = String(claims.preferred_username ?? claims.email ?? "google-user");
  return {
    username,
    token,
    claims: claims as SubjectClaims,
    summary: summarizeClearance(claims),
    expiresAt: typeof claims.exp === "number" ? claims.exp * 1000 : Date.now() + 5 * 60_000,
    name: typeof claims.name === "string" ? claims.name : undefined,
    picture: typeof claims.picture === "string" ? claims.picture : undefined,
    refreshToken,
    // Mark the session FEDERATED so the boot refresher routes its renewal through the
    // SILENT GOOGLE path (below), not idplite's refresh_token grant — federated identities
    // get NO idplite refresh token by design (re-auth goes back through Google). Set for
    // BOTH the interactive login exchange AND the silent re-auth exchange (this is the one
    // place federated sessions are minted).
    federated: true,
  };
}

// --- SILENT (non-interactive) GOOGLE RE-AUTH — federated-session renewal ----------------------
//
// THE PROBLEM. A federated (Google) session gets NO idplite refresh token by design ("re-auth
// goes back through Google"), so when its ~5-min COP access token expires the KAS 401s and the
// idplite refresh_token grant the password path uses CANNOT renew it. Rather than force a full
// interactive re-login, we SILENTLY re-verify the LIVE Google session and re-exchange it through
// the SAME stateless BFF (`/idp/federate/google`) login already uses — minting a fresh COP token
// with zero idplite/Google-console changes (confirmed: the federate endpoint is a stateless
// exchange).
//
// SECURITY POSTURE — PRESERVED, NOT WEAKENED. We do NOT mint or store an idplite refresh token
// for a federated identity. The GOOGLE SESSION stays the source of truth: every renewal re-runs
// a prompt=none authorization round, so a fresh Google credential (fresh id_token) is minted and
// re-verified SERVER-SIDE each time. Constraints the idplite owners confirmed are satisfied
// INHERENTLY by a genuine prompt=none round: (1) a FRESH credential every time — never
// cache-and-re-present (the server's replay seen-set 401s a re-presented id_token); (2) a FRESH
// per-attempt NONCE, server-bound (threaded through BOTH the Google authorization request AND the
// BFF POST — see silentGoogleReauth); (3) the code + PKCE-verifier path (preferred). The access
// token TTL/rotation is unchanged — this is TOKEN ACQUISITION ONLY; it never touches the
// PDP/KAS release decision.
//
// FAIL-CLOSED. If the Google session is gone (`prompt=none` → error=login_required), FedCM is
// unavailable, the frame is cross-origin-blocked, or the exchange rejects, we return null — the
// caller then falls back to the HONEST "SESSION EXPIRED → re-login" banner. No token is ever
// fabricated; a real re-auth failure surfaces.

/** True when THIS document is the hidden IFRAME a silent renewal loaded purely to read the fresh
 *  `?code` off the same-origin redirect landing (see iframeSilentCode). The app MUST NOT boot the
 *  COP inside that frame: the parent tab reads the code and runs the ONE BFF exchange; a second
 *  boot here would double-spend the single-use code (racing the parent) and spin up a hidden
 *  duplicate map/KAS. Detected by being framed (top !== self) AND landing on an OAuth callback —
 *  a purely identity comparison + same-origin href read, so it never touches a cross-origin
 *  property. Pure over the passed window-like handle (injectable for tests). */
export function isSilentReauthFrame(w: {
  top?: unknown;
  self?: unknown;
  location: { href: string };
}): boolean {
  // `top !== self` ⇒ we are inside a frame. Combined with an OAuth landing this is unambiguously
  // the silent-renewal probe (the app is never legitimately embedded on a `?code`/`?error` URL).
  // The landing test is inlined off readGoogleCallback (rather than importing entrance.ts's
  // isOAuthCallbackLanding) to avoid an import cycle — entrance.ts imports FROM this module.
  const framed = w.top !== undefined && w.self !== undefined && w.top !== w.self;
  const cb = readGoogleCallback(w.location.href);
  return framed && (cb.code !== undefined || cb.error !== undefined);
}

/** Obtain a fresh Google authorization code WITHOUT interactive UI. Given a prompt=none auth URL,
 *  loads it in a hidden iframe and waits for Google to redirect the frame back to our SAME-ORIGIN
 *  redirect_uri, then reads `?code`/`?error` off the landing. Resolves with the callback, or a
 *  synthetic `{ error }` on timeout / no-DOM / cross-origin-only (all treated as "needs
 *  interaction" ⇒ fall closed). This is the injectable seam a future FedCM/GIS path swaps in
 *  (same signature) — the wiring above is mechanism-agnostic. */
export type SilentCodeGetter = (
  authUrl: string,
  redirectUri: string,
  timeoutMs: number,
) => Promise<GoogleCallback>;

const iframeSilentCode: SilentCodeGetter = (authUrl, _redirectUri, timeoutMs) =>
  new Promise<GoogleCallback>((resolve) => {
    if (typeof document === "undefined" || typeof window === "undefined") {
      resolve({ error: "no_dom" });
      return;
    }
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.setAttribute("aria-hidden", "true");
    iframe.setAttribute("title", "google-silent-reauth");
    let settled = false;
    const finish = (cb: GoogleCallback): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      iframe.removeEventListener("load", onLoad);
      try {
        iframe.remove();
      } catch {
        /* already detached */
      }
      resolve(cb);
    };
    const onLoad = (): void => {
      // While the frame is on accounts.google.com, reading its location THROWS (cross-origin) —
      // swallow and keep waiting for the redirect back to our origin. Google refusing to be
      // framed (X-Frame-Options) simply never lands here ⇒ the timeout fires ⇒ fall closed.
      let href: string | null = null;
      try {
        href = iframe.contentWindow?.location?.href ?? null;
      } catch {
        return; // still cross-origin (on Google) — wait for the next load
      }
      if (!href || href === "about:blank") return;
      const cb = readGoogleCallback(href);
      // Landed same-origin: a `code` (renewed) or an `error` (login_required/…); a landing with
      // NEITHER means Google bounced us back without granting ⇒ treat as needing interaction.
      finish(cb.code || cb.error ? cb : { error: "login_required" });
    };
    const timer = window.setTimeout(() => finish({ error: "timeout" }), timeoutMs);
    iframe.addEventListener("load", onLoad);
    iframe.src = authUrl;
    document.body.appendChild(iframe);
  });

export interface SilentReauthDeps {
  storage?: Storage;
  clientId?: string;
  redirectUri?: string;
  /** The email of the session to re-verify (OAuth login_hint) — disambiguates a multi-session
   *  browser under prompt=none. */
  loginHint?: string;
  /** Obtain the fresh authorization code (default = hidden-iframe prompt=none). Injectable so
   *  the state machine is unit-testable with the Google round mocked, and so a FedCM/GIS path
   *  can be dropped in later without touching the wiring. */
  obtainSilentCode?: SilentCodeGetter;
  /** The BFF code→session exchange (default = exchangeCodeForSession, the SAME one login uses). */
  exchange?: (cb: GoogleCallback, storage: Storage) => Promise<Session>;
  /** Bound on the silent round before we give up and fall closed. */
  timeoutMs?: number;
}

/** SILENTLY re-verify the live Google session and mint a FRESH COP access token via the SAME BFF
 *  code exchange login uses — for a FEDERATED session whose access token expired. Returns the
 *  fresh Session, or null on ANY failure (Google session gone, prompt=none login_required, FedCM
 *  unavailable, cross-origin blocked, timeout, exchange rejected) so the caller falls closed to
 *  the honest re-login banner. A fresh per-attempt nonce + PKCE verifier are generated and threaded
 *  through BOTH the Google authorization request AND the BFF POST (server-bound replay/nonce
 *  defense). NEVER fabricates a token. */
export async function silentGoogleReauth(deps: SilentReauthDeps = {}): Promise<Session | null> {
  const clientId = deps.clientId ?? GOOGLE_CLIENT_ID;

  // No real Google client configured ⇒ nothing to silently renew against — fall closed. Checked
  // FIRST (before touching window via googleRedirectUri) so a mis-configured build fails closed
  // without a stray DOM/window access.
  if (isPlaceholderGoogleClient() && !deps.clientId) return null;

  const storage = deps.storage ?? sessionStorage;
  const redirectUri = deps.redirectUri ?? googleRedirectUri();
  const obtain = deps.obtainSilentCode ?? iframeSilentCode;
  const exchange = deps.exchange ?? ((cb, st) => exchangeCodeForSession(cb, { storage: st }));
  const timeoutMs = deps.timeoutMs ?? 8_000;

  // FRESH per-attempt PKCE + state (CSRF) + nonce (server-bound). Single-use; a real prompt=none
  // round returns a fresh Google credential each time — we never cache-and-re-present.
  const verifier = randomString(48);
  const codeChallenge = await pkceChallengeFromVerifier(verifier);
  const state = randomString(16);
  const nonce = randomString(16);
  storage.setItem(PKCE_KEY, JSON.stringify({ verifier, state, nonce, redirectUri }));

  const url = buildGoogleAuthUrl({
    clientId,
    redirectUri,
    state,
    nonce,
    codeChallenge,
    prompt: "none", // non-interactive: a code IFF a live Google session, else error=login_required
    loginHint: deps.loginHint,
  });

  let cb: GoogleCallback;
  try {
    cb = await obtain(url, redirectUri, timeoutMs);
  } catch {
    storage.removeItem(PKCE_KEY);
    return null; // frame threw / rejected — fall closed
  }
  if (cb.error || !cb.code) {
    storage.removeItem(PKCE_KEY); // login_required / interaction_required / timeout — fall closed
    return null;
  }
  try {
    // Reuse the SAME BFF exchange login uses: it CSRF-checks state against the stored PKCE, POSTs
    // {code, code_verifier, redirect_uri, nonce} server-side, and clears PKCE_KEY on success.
    return await exchange(cb, storage);
  } catch {
    storage.removeItem(PKCE_KEY);
    return null; // exchange rejected (replay/nonce/verify) — fall closed, never fabricate a token
  }
}

// --- THE KAS SILENT-REFRESH STATE MACHINE (branches by session KIND) -------------------------
// The single callback the boot wires as kas.setTokenRefresher (App.tsx). A PURE combinator over
// injected session seams — it lives HERE (not tdflite.ts) so it and its unit test stay KAS-FREE
// (tdflite.ts pulls kas.ts → @opentdf/sdk; this module imports only type-erased things), matching
// the node-side tsx-check discipline. kas.ts's proactive-skew refresh + reactive-401 retry +
// generation guard + the onTokenRenewed chat auto-recovery all consume this callback UNCHANGED
// for federated sessions.

const SILENT_REAUTH_COOLDOWN_MS = 30_000; // after a failed silent Google round, back off this long

/** Injectable session seams for makeTokenRefresher. App.tsx supplies tdflite's methods; the unit
 *  test supplies mocks (so the state machine is provable without React / fetch / the KAS graph). */
export interface TokenRefresherDeps {
  /** Read the LATEST persisted session (no expiry gate — the access token may BE expired). */
  peek: () => Session | null;
  /** Password/persona renewal: idplite refresh_token rotation (persists internally). */
  refresh: (s: Session) => Promise<Session | null>;
  /** Federated renewal: silent Google re-verify + BFF re-exchange (persists internally). Returns
   *  the fresh session or null. NO idplite refresh token is ever minted/stored (Google stays SoT). */
  refreshFederated: (s: Session) => Promise<Session | null>;
  /** Clock (injectable for the cooldown test). */
  now?: () => number;
}

/** Build the KAS silent-refresh callback for the CURRENT persisted session. Branches by KIND:
 *
 *   · refreshToken present (password/persona) → idplite refresh_token rotation (unchanged).
 *   · federated (Google)                      → SILENT Google re-verify (prompt=none) + the SAME
 *                                               BFF re-exchange login uses (refreshFederated). No
 *                                               idplite refresh token is minted/stored — the
 *                                               Google session stays the source of truth.
 *   · neither                                 → null (unrenewable → the honest re-login banner).
 *
 *  Returns the fresh ACCESS TOKEN, or null on ANY failure so kas.ts falls closed exactly as before
 *  (token stays stale → tokenState()="expired" → the amber SESSION EXPIRED banner). A failed
 *  federated round arms a short cooldown so a burst of KAS calls in an expired-with-dead-Google-
 *  session tab can't hammer Google / the idplite rate limit (burst 15, sustained 30/min). Never
 *  fabricates a token; never weakens the KAS gate (token acquisition only, not a release decision). */
export function makeTokenRefresher(deps: TokenRefresherDeps): () => Promise<string | null> {
  const { peek, refresh, refreshFederated } = deps;
  const now = deps.now ?? (() => Date.now());
  let silentCooldownUntil = 0;

  return async (): Promise<string | null> => {
    const cur = peek();
    if (!cur) return null;

    // PASSWORD / PERSONA: rotate the idplite refresh token. Unchanged.
    if (cur.refreshToken) {
      const next = await refresh(cur);
      return next?.token ?? null;
    }

    // FEDERATED (Google): NO idplite refresh token by design — re-verify the LIVE Google session
    // and re-exchange via the SAME stateless BFF (fresh credential + fresh nonce each round, in
    // silentGoogleReauth). On failure → null → the honest SESSION EXPIRED banner.
    if (cur.federated) {
      if (now() < silentCooldownUntil) return null; // backing off after a recent silent failure
      const next = await refreshFederated(cur);
      if (!next) {
        silentCooldownUntil = now() + SILENT_REAUTH_COOLDOWN_MS;
        return null; // Google session gone / FedCM unavailable / frame blocked → fall closed
      }
      silentCooldownUntil = 0;
      return next.token; // fresh token adopted (refreshFederated already persisted the session)
    }

    return null; // unrenewable session (no refresh token, not federated) → honest banner
  };
}
